// data/params props

export interface UpdateBoardUsersParamsProps {
	data: { userId: string }[]
	id: string
	invalidate?: boolean
}

export interface UpdateStatusParamsProps {
	data: {
		name: string
		color: string
		complete: boolean
	}
	statusId: string
	boardId: string
}

export interface UpdateCardParamsProps {
	data: {
		statusId: string
		assignedToId: string
		title: string
		content: string
		dueDate: string | Date | null
		priority: number
		files?: string[]
	}
	cardId: string
	boardId: string
}

export type DefaultStatusParamsProps = Omit<UpdateStatusParamsProps, 'data'>
export type DefaultCardParamsProps = Omit<UpdateCardParamsProps, 'data'>

export interface MoveStatusDataProps {
	position: number
}

export interface MoveCardDataProps {
	position: number
	statusId: string
}

export interface DefaultCardCommentDataProps {
	content: string
	parentId?: string | null
}

export interface UpdateCardCommentParamsProps {
	data: DefaultCardCommentDataProps
	boardId: string
	cardId: string
	commentId: string
	parentId?: string | null
}

export type DefaultCardCommentParamsProps = Omit<
	UpdateCardCommentParamsProps,
	'data'
>

// items
export const CardPriorities = {
	1: 'low',
	2: 'medium',
	3: 'high',
} as const

export interface BoarFile {
	id: string
	name: string
}

export interface BoardListItem {
	id: string
	name: string
	users: number
	cards: number
	files: BoarFile[]
	createdBy: BoardUser
}

export interface BoardUser {
	id: string
	name: string
	email: string
	avatar: string | null
}

export interface BoardSharedUser {
	id: string
	firstName: string
	lastName: string
	fullName: string
	email: string
	avatar: string
	createdAt: string
	modifiedAt: string
	organization: {
		id: string
		name: string
		rootFolderId: string
		sharedFolderId: string
		slug: string
		logo: string
		contactEmail: string
	}
	roles: Array<{
		id: string
		name: string
		organizationId: string
		canDelete: boolean
		isAdministrator: boolean
	}>
	groups: Array<{
		id: string
		name: string
		organizationId: string
	}>
	phone: string
	userFolderId: string
	isDeactivated: boolean
	isDeleted: boolean
	invite: {
		sentAt: string
		acceptedAt: string
	}
	identity: {
		userId: string
		provider: string
		connection: string
		isSocial: boolean | null
		nameIdentifier: string
	}
	preferences: {
		language: string | null
		timezone: string
		theme: string | null
		dateFormat: string | null
		tables: Array<{
			name: string
			columns: string[]
		}>
	}
	notifications: {
		accessAndPermissions: boolean
		contentActivity: boolean
		calendarAndEvents: boolean
		inboxAndDirect: boolean
		adminAndSystem: boolean
		favoriteFiles: boolean
		sharesAndInvitations: boolean
		taskAssigned: boolean
		taskDeadline: boolean
	}
	plan: {
		name: string
		features: number
	}
	isAdministrator: boolean
}

export interface BoardStatus {
	id: string
	name: string
	color?: string
	complete: boolean
	cards: BoardStatusCard[]
}

export interface BoardStatusCard {
	id: string
	title: string
	content: string
	priority: number // 0 - low, 1 - medium, 2 - high
	createdBy: BoardUser
	assignedTo: BoardUser
	createdAt: Date | string
	updatedAt?: Date | string | null
	completedAt?: Date | string | null
	dueDate?: Date | string | null
	statusId: string
	initialStatusId: string | null
	files?: BoarFile[]
}

export interface Board {
	id: string
	name: string
	users: BoardUser[]
	statuses: BoardStatus[]
	createdBy: BoardUser
	files?: BoarFile[]
}

export interface CardStatus {
	id: string
	name: string
	color: string
}

export interface CardUser {
	id: string
	name: string
	email: string
	avatar: string
}

export interface Card {
	id: string
	title: string
	content: string
	createdAt: string | Date
	modifiedAt: string | Date | null
	completedAt: string | Date | null
	dueDate: string | Date | null
	status: CardStatus
	createdBy: CardUser
	assignedTo: CardUser | null
}

export interface CardComment {
	id: string
	content: string
	createdBy: CardUser
	createdAt: string | Date
	modifiedAt: string | Date | null
	parentId: string
}

// responses

// boards
export interface GetBoardsDataResponse {
	data: BoardListItem[]
	success: boolean
}

export interface GetBoardDataResponse {
	data: Board
	success: boolean
}

export interface GetBoardPermissionsDataResponse {
	data: { user: BoardSharedUser }[]
	success: boolean
}

// statuses
export interface GetBoardStatusesDataResponse {
	data: Omit<BoardStatus, 'card'>[]
	success: boolean
}

// cards
export interface GetStatusCardDataResponse {
	data: BoardStatusCard
	success: boolean
	errors: string[]
}

// card comments
export interface GetCardCommentDataResponse {
	data: CardComment[]
	success: boolean
	errors: string[]
}

export interface CreateCommentPostResponse {
	data: CardComment
	success: boolean
}
